import {
  type DataLayerUserData,
  generateEventId,
  markOnceInSession,
  pushToDataLayer,
} from './datalayer';
import { conversionContext } from './datalayer-meta';

/**
 * EU-391 — Eventos do **dataLayer V2** (GTM & APIs de Conversão). Único lar dos
 * pushes de evento do candidate (o `gtm.ts` só carrega o container; os helpers
 * vivem no `datalayer.ts`).
 *
 * A config dos eventos é DRIVEN-BY-DATALAYER: o GTM lê o `event` empurrado aqui
 * e dispara as tags do GA4/Meta/Google Ads (não há mais o evento legado
 * `conta-criacao` mapeado por trigger no painel — usamos os nomes padrão do spec
 * direto: `sign_up`, `login`, `view_job`, etc).
 *
 * Cada função monta o payload no contrato do spec e empurra no `window.dataLayer`
 * (via `pushToDataLayer`, que é no-op sem container GTM). Os eventos de
 * disparo-único recebem um `dedupeKey` e usam `markOnceInSession` — re-render
 * e reload no mesmo tab NÃO redisparam; o backstop cross-sessão é o `event_id`
 * (dedup Web/Server).
 *
 * Convenção V2: `opportunity_*` = vaga (job); `career_*` = projeto/programa.
 * Campos não coletados vão como `null` (nunca `""`).
 */

/**
 * `method` dos eventos de auth, em casing canônico GA4 (provider name humano):
 * `email`/`Google`/`LinkedIn`. Derivado do token via `detectSignupMethod`.
 */
export type SignupMethod = 'email' | 'Google' | 'LinkedIn';

export interface SignUpInput {
  method: SignupMethod;
  participant_id: string;
  user_data: DataLayerUserData;
  /** Dedup one-shot por cadastro (DLR-022). */
  dedupeKey?: string;
}

/**
 * EU-395 / item 3 do spec — `sign_up`: o usuário cria a conta no SSO e volta ao
 * app **logado pela primeira vez**. Carrega `event_id` (único, dedup Web/Server),
 * `method`, `participant_id` e `user_data`. Endereço/telefone/db/ge ainda não
 * foram coletados nesta fase → saem como `null` (premissa do spec: "endereço
 * apenas se já coletado"). NÃO dispara em login recorrente (gateado no call-site
 * pela flag `signup-welcome`).
 */
export function pushSignUp(input: SignUpInput): void {
  if (input.dedupeKey && !markOnceInSession(input.dedupeKey)) return;
  pushToDataLayer({
    event: 'sign_up',
    event_id: generateEventId(),
    method: input.method,
    participant_id: input.participant_id,
    user_data: input.user_data,
    // EU-587 — Advanced Matching: fbc/fbp (cookie ou fbclid persistido) +
    // event_source_url. O hash do user_data acontece no GTM server-side.
    ...conversionContext(),
  });
}

/** EU-392 / DLR-001 — `virtual_pageview` a cada mudança de rota (substitui o PageView nativo). */
export function pushVirtualPageview(pagePath: string, pageTitle: string): void {
  pushToDataLayer({
    event: 'virtual_pageview',
    page_path: pagePath,
    page_title: pageTitle,
  });
}

export interface ViewJobInput {
  opportunity_id: string;
  opportunity_name: string | null;
  career_id: string | null;
  career_name: string | null;
  /** Dedup one-shot por vaga (DLR-012). */
  dedupeKey?: string;
}

/** EU-393 — `view_job` ao carregar a página de detalhes da vaga. */
export function pushViewJob(input: ViewJobInput): void {
  if (input.dedupeKey && !markOnceInSession(input.dedupeKey)) return;
  pushToDataLayer({
    event: 'view_job',
    opportunity_id: input.opportunity_id,
    opportunity_name: input.opportunity_name ?? null,
    career_id: input.career_id ?? null,
    career_name: input.career_name ?? null,
  });
}

export interface LoginInput {
  method: SignupMethod;
  participant_id: string;
  user_data: DataLayerUserData;
  /** Dedup one-shot por login (DLR-021). */
  dedupeKey?: string;
}

/**
 * EU-395 — `login` de usuário já existente que retorna autenticado. Carrega
 * `event_id` (o payload do spec inclui `event_id: 'login_'+...`), `method`,
 * `participant_id` e `user_data` mínimo (email).
 */
export function pushLogin(input: LoginInput): void {
  if (input.dedupeKey && !markOnceInSession(input.dedupeKey)) return;
  pushToDataLayer({
    event: 'login',
    event_id: generateEventId(),
    method: input.method,
    participant_id: input.participant_id,
    user_data: input.user_data,
  });
}

export interface GenerateLeadInput {
  opportunity_id: string;
  opportunity_name: string | null;
  career_name: string | null;
  participant_id: string | null;
  user_data: DataLayerUserData;
  /** Dedup one-shot por inscrição (DLR-034). */
  dedupeKey?: string;
}

/**
 * EU-398 — `generate_lead` (conversão principal). Disparar APENAS na conclusão
 * confirmada da inscrição (nunca no clique). Carrega `event_id`, contexto da
 * vaga, `participant_id` e `user_data` completo.
 */
export function pushGenerateLead(input: GenerateLeadInput): void {
  if (input.dedupeKey && !markOnceInSession(input.dedupeKey)) return;
  pushToDataLayer({
    event: 'generate_lead',
    event_id: generateEventId(),
    opportunity_id: input.opportunity_id,
    opportunity_name: input.opportunity_name ?? null,
    career_name: input.career_name ?? null,
    participant_id: input.participant_id ?? null,
    user_data: input.user_data,
    // EU-587 — Advanced Matching: fbc/fbp (cookie ou fbclid persistido) +
    // event_source_url. O hash do user_data acontece no GTM server-side.
    ...conversionContext(),
  });
}

export interface BeginSelectionStageInput {
  opportunity_id: string;
  opportunity_name: string | null;
  stage_name: string | null;
  /** Dedup one-shot por vaga (DLR-044). */
  dedupeKey?: string;
}

/** EU-399 — `begin_selection_stage` quando o candidato inicia a etapa de seleção. */
export function pushBeginSelectionStage(input: BeginSelectionStageInput): void {
  if (input.dedupeKey && !markOnceInSession(input.dedupeKey)) return;
  pushToDataLayer({
    event: 'begin_selection_stage',
    opportunity_id: input.opportunity_id,
    opportunity_name: input.opportunity_name ?? null,
    stage_name: input.stage_name ?? null,
  });
}

export interface QualifiedLeadInput {
  opportunity_id: string;
  opportunity_name: string | null;
  career_name: string | null;
  /** Nome da etapa de seleção. Campo `stage_name` no payload (spec EU-399 atualizado). */
  stage_name: string | null;
  participant_id: string | null;
  user_data: DataLayerUserData;
  /** Dedup one-shot por vaga (DLR-044). */
  dedupeKey?: string;
}

/**
 * EU-399 — `qualified_lead` quando o candidato conclui 100% a etapa de seleção
 * (conversão). Carrega `event_id`, contexto da vaga, `stage_name`,
 * `participant_id` e `user_data` completo.
 */
export function pushQualifiedLead(input: QualifiedLeadInput): void {
  if (input.dedupeKey && !markOnceInSession(input.dedupeKey)) return;
  pushToDataLayer({
    event: 'qualified_lead',
    event_id: generateEventId(),
    opportunity_id: input.opportunity_id,
    opportunity_name: input.opportunity_name ?? null,
    career_name: input.career_name ?? null,
    stage_name: input.stage_name ?? null,
    participant_id: input.participant_id ?? null,
    user_data: input.user_data,
  });
}
